import {
    createControlPanelUser,
    deleteControlPanelUser,
    listControlPanelUsers,
    updateControlPanelUser,
} from "@/api/controlPanelUsers";
import { listGrantGroups } from "@/api/grantGroups";
import { toast } from "@/shared/hooks/useToast";
import { useCallback, useMemo, useState } from "react";
import type {
    ControlPanelGrantGroup,
    ControlPanelUserGrantsMap,
    ControlPanelUserModalMode,
    ControlPanelUserRow,
    UpsertControlPanelUserDto,
} from "../types/controlPanelUser";


function joinGroupNames(groupIds: number[], groups: ControlPanelGrantGroup[]) {
    const idToName = new Map(groups.map((g) => [g.id, g.name] as const));
    return groupIds
        .map((id) => idToName.get(id))
        .filter((v): v is string => !!v)
        .sort((a, b) => a.localeCompare(b))
        .join(", ");
}

export function useControlPanelUsers() {

    const [isLoading, setIsLoading] = useState(false);

    const [allGrantGroups, setAllGrantGroups] = useState<ControlPanelGrantGroup[]>([]);
    const [userGrantsMap, setUserGrantsMap] = useState<ControlPanelUserGrantsMap>({});
    const [rows, setRows] = useState<ControlPanelUserRow[]>([]);

    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<ControlPanelUserModalMode>("추가");
    const [editingUserId, setEditingUserId] = useState<number | null>(null);

    const [form, setForm] = useState<UpsertControlPanelUserDto>({
        userEmail: "",
        grantGroupIds: [],
    });

    const resetForm = useCallback(() => {
        setForm({ userEmail: "", grantGroupIds: [] });
    }, []);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const [{ users }, { grant_groups }] = await Promise.all([listControlPanelUsers(), listGrantGroups()]);

            const groups: ControlPanelGrantGroup[] = (grant_groups ?? [])
                .map((g) => ({ id: g.id, name: g.name }))
                .sort((a, b) => a.name.localeCompare(b.name));

            const grants: ControlPanelUserGrantsMap = Object.fromEntries(users.map((u) => [u.id, u.grant_ids ?? []] as const));

            setAllGrantGroups(groups);
            setUserGrantsMap(grants);

            setRows(
                users.map((u) => ({
                    id: u.id,
                    userEmail: u.email,
                    grant: joinGroupNames(u.grant_ids ?? [], groups),
                }))
            );

            setSelectedUserIds([]);
            resetForm();
            setModalOpen(false);
            setEditingUserId(null);
            setModalMode("추가");
        } catch (e) {
            toast({
                title: "로드 실패",
                description: e instanceof Error ? e.message : "유저 목록을 불러오지 못했습니다.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    }, [resetForm]);

    const openAdd = useCallback(() => {
        setModalMode("추가");
        setEditingUserId(null);
        resetForm();
        setModalOpen(true);
    }, [resetForm]);

    const openEditByRow = useCallback(
        (row: ControlPanelUserRow) => {
            setModalMode("수정");
            setEditingUserId(row.id);
            setForm({
                userEmail: row.userEmail,
                grantGroupIds: userGrantsMap[row.id] ?? [],
            });
            setModalOpen(true);
        },
        [userGrantsMap]
    );

    const closeModal = useCallback(() => {
        setModalOpen(false);
        resetForm();
        setEditingUserId(null);
        setModalMode("추가");
    }, [resetForm]);

    const toggleGrantGroup = useCallback((groupId: number) => {
        setForm((prev) => {
            const has = prev.grantGroupIds.includes(groupId);
            return {
                ...prev,
                grantGroupIds: has ? prev.grantGroupIds.filter((id) => id !== groupId) : [...prev.grantGroupIds, groupId],
            };
        });
    }, []);

    const submit = useCallback(async () => {
        const email = form.userEmail.trim();
        if (!email) {
            toast({ title: "입력 오류", description: "유저 이메일은 필수입니다.", variant: "destructive" });
            return;
        }

        setIsLoading(true);
        try {
            if (modalMode === "추가") {
                const { user: created } = await createControlPanelUser({
                    email,
                    grant_ids: form.grantGroupIds,
                });

                setUserGrantsMap((prev) => ({ ...prev, [created.id]: created.grant_ids ?? [] }));
                setRows((prev) =>
                    [...prev, { id: created.id, userEmail: created.email, grant: joinGroupNames(created.grant_ids ?? [], allGrantGroups) }].sort(
                        (a, b) => a.id - b.id
                    )
                );

                toast({ title: "완료", description: "유저가 추가되었습니다." });
            } else {
                if (editingUserId == null) return;

                const { user: updated } = await updateControlPanelUser(editingUserId, {
                    email,
                    grant_ids: form.grantGroupIds,
                });

                setUserGrantsMap((prev) => ({ ...prev, [updated.id]: updated.grant_ids ?? [] }));
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === updated.id
                            ? { ...r, userEmail: updated.email, grant: joinGroupNames(updated.grant_ids ?? [], allGrantGroups) }
                            : r
                    )
                );

                toast({ title: "완료", description: "유저가 수정되었습니다." });
            }

            closeModal();
        } catch (e) {
            toast({
                title: "요청 실패",
                description: e instanceof Error ? e.message : "처리 중 오류가 발생했습니다.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    }, [form, modalMode, editingUserId, allGrantGroups, closeModal]);

    const deleteSelected = useCallback(async () => {
        if (selectedUserIds.length === 0) return;

        setIsLoading(true);
        try {
            await Promise.all(selectedUserIds.map((id) => deleteControlPanelUser(id)));

            setRows((prev) => prev.filter((r) => !selectedUserIds.includes(r.id)));
            setUserGrantsMap((prev) => {
                const next = { ...prev };
                selectedUserIds.forEach((id) => delete next[id]);
                return next;
            });
            setSelectedUserIds([]);

            toast({ title: "삭제 완료", description: "선택된 유저가 삭제되었습니다." });
        } catch (e) {
            toast({
                title: "삭제 실패",
                description: e instanceof Error ? e.message : "삭제 중 오류가 발생했습니다.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    }, [selectedUserIds]);

    const summary = useMemo(
        () => ({ total: rows.length, selected: selectedUserIds.length }),
        [rows.length, selectedUserIds.length]
    );

    return {
        isLoading,

        allGrantGroups,
        rows,

        selectedUserIds,
        setSelectedUserIds,

        modalOpen,
        modalMode,
        form,
        setForm,
        toggleGrantGroup,

        summary,

        load,
        openAdd,
        openEditByRow,
        closeModal,
        submit,
        deleteSelected,
    };
}